import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ImagePlus, RotateCcw, RefreshCw, Check } from "lucide-react";

const OUT_W = 1200, OUT_H = 900, MAX_BYTES = 12 * 1024 * 1024;

// Pick a photo (phone or computer), pan/zoom it inside a 4:3 frame, export the crop and upload it
export function PhotoDialog({ recipe, open, onClose, onSaved }) {
  const { t, tv } = useLang();
  const [file, setFile] = useState(null);
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const parseFocal = (f) => { const m = /(\d+)%\s+(\d+)%/.exec(f || ""); return m ? { x: +m[1], y: +m[2] } : { x: 50, y: 50 }; };
  const [focal, setFocal] = useState({ x: 50, y: 50 });
  const focalDrag = useRef(false);
  const drag = useRef(null);
  const inputRef = useRef(null);
  const frameRef = useRef(null);
  const [fw, setFw] = useState(480);
  const FRAME_W = fw, FRAME_H = Math.round(fw * 0.75);
  useEffect(() => {
    if (!img || !frameRef.current) return undefined;
    const ro = new ResizeObserver(([e]) => setFw(Math.round(e.contentRect.width) || 480));
    ro.observe(frameRef.current);
    return () => ro.disconnect();
  }, [img]);

  useEffect(() => { if (!open) { setFile(null); setImg(null); setZoom(1); setPos({ x: 0, y: 0 }); } else { setFocal(parseFocal(recipe?.focal)); } }, [open, recipe?.focal]);

  const setFocalFromEvent = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setFocal({ x: Math.round(Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100))), y: Math.round(Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100))) });
  };

  const pick = (f) => {
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error(t("photo_too_large")); return; }
    const url = URL.createObjectURL(f);
    const image = new Image();
    image.onload = () => { setImg(image); setFile(f); setZoom(1); setPos({ x: 0, y: 0 }); };
    image.src = url;
  };

  // base scale = cover the frame without distortion; zoom multiplies it
  const base = img ? Math.max(FRAME_W / img.width, FRAME_H / img.height) : 1;
  const scale = base * zoom;
  const clamp = (p, z = zoom) => {
    if (!img) return p;
    const s = base * z, maxX = Math.max(0, (img.width * s - FRAME_W) / 2), maxY = Math.max(0, (img.height * s - FRAME_H) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, p.x)), y: Math.min(maxY, Math.max(-maxY, p.y)) };
  };
  const onDown = (e) => { drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const onMove = (e) => { if (drag.current) setPos(clamp({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y })); };
  const onUp = () => { drag.current = null; };
  const changeZoom = (z) => { setZoom(z); setPos((p) => clamp(p, z)); };

  const exportBlob = () => new Promise((resolve) => {
    const c = document.createElement("canvas");
    c.width = OUT_W; c.height = OUT_H;
    const ctx = c.getContext("2d");
    const k = OUT_W / FRAME_W;
    const w = img.width * scale * k, h = img.height * scale * k;
    ctx.drawImage(img, (OUT_W - w) / 2 + pos.x * k, (OUT_H - h) / 2 + pos.y * k, w, h);
    c.toBlob(resolve, "image/jpeg", 0.92);
  });

  const save = async () => {
    setBusy(true);
    try {
      const blob = await exportBlob();
      const fd = new FormData();
      fd.append("file", blob, (file?.name || "photo").replace(/\.[^.]+$/, "") + ".jpg");
      fd.append("focal_x", String(focal.x));
      fd.append("focal_y", String(focal.y));
      const { data } = await api.post(`/admin/recipes/${recipe.recipe_id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("photo_updated"));
      onSaved(data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const saveFocal = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/recipes/${recipe.recipe_id}/focal`, focal);
      toast.success(t("focal_saved"));
      onSaved(data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/recipes/${recipe.recipe_id}/image`);
      toast.success(t("photo_restored"));
      onSaved(data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  if (!recipe) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="photo-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("change_photo")}</DialogTitle>
          <DialogDescription>{tv(recipe.name)}</DialogDescription>
        </DialogHeader>

        <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="photo-input" onChange={(e) => pick(e.target.files?.[0])} />

        {!img ? (
          <div className="space-y-3">
            <div className="relative w-full overflow-hidden rounded-2xl bg-chenia-espresso cursor-crosshair touch-none select-none" style={{ aspectRatio: "4 / 3" }} data-testid="focal-picker"
              onPointerDown={(e) => { focalDrag.current = true; e.currentTarget.setPointerCapture(e.pointerId); setFocalFromEvent(e); }}
              onPointerMove={(e) => { if (focalDrag.current) setFocalFromEvent(e); }}
              onPointerUp={() => { focalDrag.current = false; }} onPointerCancel={() => { focalDrag.current = false; }}>
              <img src={recipe.image} alt="" draggable={false} className="h-full w-full object-cover" style={{ objectPosition: `${focal.x}% ${focal.y}%` }} />
              <span className="absolute h-7 w-7 -ml-3.5 -mt-3.5 rounded-full border-[3px] border-white shadow-lg bg-primary/70 pointer-events-none" style={{ left: `${focal.x}%`, top: `${focal.y}%` }} data-testid="focal-dot" />
            </div>
            <p className="text-xs text-muted-foreground">{t("focal_hint")} <span className="font-medium" data-testid="focal-value">{focal.x}% / {focal.y}%</span></p>
            <button onClick={() => inputRef.current?.click()} data-testid="photo-choose"
              className="w-full rounded-2xl border-2 border-dashed border-border bg-secondary/40 hover:bg-secondary transition-colors flex items-center gap-3 px-4 py-3 text-left">
              <ImagePlus className="h-6 w-6 text-primary shrink-0" />
              <span className="min-w-0"><span className="font-medium block">{t("choose_photo")}</span><span className="text-xs text-muted-foreground">{t("photo_hint")}</span></span>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{t("adjust_framing")}</p>
            <div ref={frameRef} className="relative w-full overflow-hidden rounded-2xl bg-chenia-espresso touch-none select-none cursor-grab active:cursor-grabbing"
              style={{ aspectRatio: "4 / 3" }} data-testid="photo-cropper"
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
              <img src={img.src} alt="" draggable={false} className="absolute max-w-none"
                style={{ width: img.width * scale, height: img.height * scale, left: (FRAME_W - img.width * scale) / 2 + pos.x, top: (FRAME_H - img.height * scale) / 2 + pos.y }} />
            </div>
            <div className="flex items-center gap-3 px-1">
              <span className="text-xs text-muted-foreground w-10">{t("zoom")}</span>
              <Slider value={[zoom]} min={1} max={3} step={0.01} onValueChange={([z]) => changeZoom(z)} data-testid="photo-zoom" />
              <Button variant="ghost" size="sm" onClick={() => inputRef.current?.click()} className="rounded-full" data-testid="photo-rechoose"><ImagePlus className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground">{t("focal_point")} : <span className="font-medium" data-testid="focal-value">{focal.x}% / {focal.y}%</span> — {t("focal_hint")}</p>
            <div className="relative w-full overflow-hidden rounded-2xl cursor-crosshair touch-none select-none" style={{ aspectRatio: "4 / 3", maxHeight: 140 }} data-testid="focal-picker"
              onPointerDown={(e) => { focalDrag.current = true; e.currentTarget.setPointerCapture(e.pointerId); setFocalFromEvent(e); }}
              onPointerMove={(e) => { if (focalDrag.current) setFocalFromEvent(e); }}
              onPointerUp={() => { focalDrag.current = false; }} onPointerCancel={() => { focalDrag.current = false; }}>
              <img src={img.src} alt="" draggable={false} className="h-full w-full object-cover" style={{ objectPosition: `${focal.x}% ${focal.y}%` }} />
              <span className="absolute h-6 w-6 -ml-3 -mt-3 rounded-full border-[3px] border-white shadow-lg bg-primary/70 pointer-events-none" style={{ left: `${focal.x}%`, top: `${focal.y}%` }} data-testid="focal-dot" />
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {img ? (
            <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-chenia-terracottadark flex-1" data-testid="photo-save">
              {busy ? <><RefreshCw className="h-4 w-4 animate-spin mr-1" /> {t("uploading")}</> : <><Check className="h-4 w-4 mr-1" /> {t("save_photo")}</>}
            </Button>
          ) : (
            <Button onClick={saveFocal} disabled={busy} className="rounded-full bg-primary hover:bg-chenia-terracottadark flex-1" data-testid="focal-save">
              {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> {t("save_focal")}</>}
            </Button>
          )}
          {recipe.custom_photo && (
            <Button variant="ghost" onClick={restore} disabled={busy} className="rounded-full text-muted-foreground" data-testid="photo-restore"><RotateCcw className="h-4 w-4 mr-1" /> {t("restore_photo")}</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
